<script setup lang="ts">
import { BsTabs } from '@afo/bsf-core'
import type { IEmits, ITab } from './KeyPolicies.types'
import { useStrategy } from '@/domains/esg/composables/content/useStrategy'
import Source from '~/domains/esg/components/Source/Source.vue'
import { useFileList } from '@/domains/investor/components/FileList/FileList.composable'
import type { IRawFileAsset, IRawFileItem } from '@/domains/investor/components/FileItem/FileItem.types'
import { getLocalizedValue, getPreferredLocale } from '@/domains/investor/utils/utils'
const { keyPoliciesTabs } = useStrategy()
const { locale } = useI18n()
const { getFileList, getFileItems } = useFileList()
const { data: policiesFileList } = await getFileList('esg-kluczowe-polityki')

const policyLinks = computed(() => {
  const contentfulLocale = getPreferredLocale(locale.value)

  return (getFileItems(policiesFileList.value) as IRawFileItem[]).map(({ fields }) => ({
    text: getLocalizedValue(fields?.title, contentfulLocale) as string,
    link: (getLocalizedValue(fields?.file, contentfulLocale) as IRawFileAsset[] | undefined)?.[0]?.url,
  }))
})

const emit = defineEmits<IEmits>()
const selectedTab = ref<ITab>(keyPoliciesTabs[0])

</script>

<template>
  <UiSection
    id="key-activities"
    color="gray"
  >
    <UiHeader
      position="center"
      :header="$t('strategy.keyPolicies.header')"
    />

    <div class="section__container">
      <ClientOnly>
        <BsTabs
          :tabs="keyPoliciesTabs"
          :model-value="selectedTab"
          :size="'large'"
          @update:model-value="(item: ITab) => {
            selectedTab = item
            emit('update:modelValue', item)
          }"
        >
          <template #environmental>
            <div
              v-if="policyLinks.length > 0"
              class="section__row"
            >
              <div class="section__col section__col--links">
                <a
                  v-for="(link, index) in policyLinks"
                  :key="`${keyPoliciesTabs[0].slotName}-link-${index}`"
                  class="section__col--link"
                  :href="link.link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ link.text }}
                </a>
              </div>
            </div>
            <div class="section__row">
              <div class="section__col section__col--img">
                <picture>
                  <source
                    media="(width < 1200px)"
                    :srcset="$t('strategy.keyPolicies.e.picture.mobile.url')"
                  >

                  <img
                    width="100%"
                    :src="$t('strategy.keyPolicies.e.picture.desktop.url')"
                    :alt="$t('strategy.keyPolicies.e.subheader')"
                  >
                </picture>

                <Source
                  :text="$t('strategy.keyPolicies.e.source.text')"
                  :link="$t('strategy.keyPolicies.e.source.link')"
                />
              </div>
            </div>

            <div class="section__row">
              <div class="section__col section__col--legend">
                <ul class="section__legend">
                  <li
                    v-for="(item, index) in keyPoliciesTabs[0].content?.legend?.items"
                    :key="`key-policies-list-item-${index}`"
                  >
                    <i :style="`background-color: ${item.color}`" />
                    {{ item.text }}
                  </li>
                </ul>
              </div>
            </div>
          </template>

          <template #social>
            <div class="section__container">
              <div
                v-if="policyLinks.length > 0"
                class="section__row"
              >
                <div class="section__col section__col--links">
                  <a
                    v-for="(link, index) in policyLinks"
                    :key="`${keyPoliciesTabs[1].slotName}-link-${index}`"
                    class="section__col--link"
                    :href="link.link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ link.text }}
                  </a>
                </div>
              </div>
              
              <div class="section__row section__row--social">
                <div class="section__col">
                  <img
                    :alt="$t('strategy.keyPolicies.s.tab')"
                    :src="$t('strategy.keyPolicies.s.picture.desktop.url')"
                  >
                </div>

                <div class="section__col section__col--legend">
                  <div class="report-legend">
                    <h4>{{ $t('strategy.keyPolicies.s.legend.text1') }}</h4>

                    <div class="report-legend__items">
                      <div
                        v-for="(item, index) in keyPoliciesTabs[1].content?.legend?.items"
                        :key="`key-policies-legend-item-${index}`"
                        class="report-legend__item"
                      >
                        <div class="report-legend__item-cell report-legend__item-cell--icon">
                          <img :src="item.icon">
                        </div>

                        <div class="report-legend__item-cell report-legend__item-cell--country">
                          {{ item.text }}
                        </div>

                        <div class="report-legend__item-cell report-legend__item-cell--score">
                          {{ item.score }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="section__row section__row--social">
                <div class="section__col">
                  <Source
                    :text="$t('strategy.keyPolicies.s.source.text')"
                    :link="$t('strategy.keyPolicies.s.source.link')"
                  />
                </div>
              </div>
            </div>
          </template>

          <template #governance>
            <div class="section__container">
              <div
                v-if="policyLinks.length > 0"
                class="section__row"
              >
                <div class="section__col section__col--links">
                  <a
                    v-for="(link, index) in policyLinks"
                    :key="`${keyPoliciesTabs[2].slotName}-link-${index}`"
                    class="section__col--link"
                    :href="link.link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ link.text }}
                  </a>
                </div>
              </div>

              <div class="section__row section__row--governance">
                <div class="section__col">
                  <img
                    :alt="$t('strategy.keyPolicies.g.tab')"
                    src="/images/strategy/benefit-systems-key-policies-g.webp"
                  >
                </div>

                <div class="section__col section__col--legend">
                  <div class="negative-effect">
                    <h3 class="negative-effect__heading">
                      {{ $t('strategy.keyPolicies.g.legend.text1') }}
                    </h3>

                    <div class="negative-effect__row">
                      <div
                        v-for="(legendItems, index) in keyPoliciesTabs[2].content?.legend"
                        :key="`negative-effect__col-${index}`"
                        class="negative-effect__col"
                        :class="`negative-effect__col--${index}`"
                      >
                        <template
                          v-for="(item, i) in legendItems.items"
                          :key="`negative-effect__t-${i}`"
                        >
                          <div class="negative-effect__spacer" />

                          <div
                            class="negative-effect__item"
                            :class="`negative-effect__item--${i}`"
                            v-html="item.text"
                          />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="section__row section__row--governance-source">
                <div class="section__col">
                  <Source
                    :text="$t('strategy.keyPolicies.g.source.text')"
                    :link="$t('strategy.keyPolicies.g.source.link')"
                  />
                </div>

                <div class="section__col">
                  <Source
                    :text="$t('strategy.keyPolicies.g.source1.text')"
                    :link="$t('strategy.keyPolicies.g.source1.link')"
                  />
                </div>
              </div>
            </div>
          </template>
        </BsTabs>
      </ClientOnly>
    </div>
  </UiSection>
</template>

<style lang="scss" scoped>
:deep(.ui-header) {
  margin-bottom: 3.2rem;
}

:deep(.bs-tabs__list) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  margin: 0 auto 3.2rem auto;
  padding: 0.8rem;
  background-color: transparent;
  border-radius: 10rem;
}

:deep(.bs-tabs__button) {
  padding: 0.8rem 3.2rem;
  border-bottom: none;
  border-radius: 10rem;
  transition: all 0.3s ease;

  @include body-2();

  &:hover {
    background-color: $gray-100;
  }
}

:deep(.bs-tabs__button--selected) {
  color: $white;
  background-color: $black;
  border-bottom: none;

  &:hover {
    color: $white;
    background-color: $black;
  }
}

.section__row--social {
  align-items: center;
  margin-bottom: 0;
}

.section__row--governance {
  align-items: center;
  margin-bottom: 0;
}

.section__row--governance-source {
  align-items: center;
  gap:0;
  margin-top: 1.6rem;
}

.section__col--img {
  text-align: center;
  img {
    max-width: 68.8rem;
  }
}

.section__col--links {
  text-align: center;
}

.section__col--link {
  @include h10;
  display: block;
  margin: 0.8rem 1.6rem;
  text-decoration: underline;
  text-decoration-color: $purple-400;
}

.section__col--legend {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;

  ul {
    margin: 0 auto;

    li {
      @include body-2();

      i {
        display: inline-block;
        width: 1.2rem;
        height: 1.2rem;
        margin-right: 0.8rem;
        border: 1px solid #2F6B8C;
        border-radius: 50%;
      }
    }
  }
}

.report-legend {
  max-width: 44rem;
  padding: 3.2rem;
  background-color: $white;
  border: 1px solid $gray-300;
  border-radius: 1rem;

  h4 {
    @include h10;
    margin-bottom: 2rem;
    color: $darkblue-400;
  }

  &__items {
    background-color: transparent;
  }

  &__item {
    display: flex;
    align-items: center;
  }

  &__item-cell {
    display: flex;
    flex: 1;
    align-items: center;
    color: $darkblue-400;
    text-align: left;
    @include h10;

    &--icon {
      img {
        width: 3.2rem;
        height: 3.2rem;
      }
    }

    &--country {
      flex: 2;
      color: $text-200;
    }

    &--score {
      justify-content: flex-end;
      text-align: right;
    }
  }
}

.negative-effect {
  $root: &;

  &__heading {
    @include h7;
    width: 100%;
    margin: 0 auto 0 auto !important;
    padding: 3.2rem;
    color: $white;
    text-align: center;
    background-color: $purple-300;
    border-radius: 1rem;
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__col {
    flex: 1;
  }

  &__item {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    padding: 1.6rem;
    color: $white;
    text-align: center;
    border-radius: 1rem;
    @include body-3;

    &--0 {
      width: 80%;
      padding: 2.4rem;
      background-color: $purple-400;
    }

    &--1 {
      width: 60%;
      background-color: $purple-500;
    }

    :deep(ul) {
      padding-left: 1.6rem;
      text-align: left;
      list-style: disc;
    }
  }

  &__spacer {
    position: relative;
    width: 1px;
    height: 5rem;
    margin: 0 auto;
    border-left: 1px dashed $gray-500;

    &:before {
      position: absolute;
      bottom: 0;
      left: -5px;
      display: block;
      width: 10px;
      height: 33%;
      background: linear-gradient(to bottom, transparent, white);
      content: ' ';
    }

    &:after {
      position: absolute;
      bottom: top;
      left: -5px;
      display: block;
      width: 10px;
      height: 33%;
      background: linear-gradient(to top, transparent, white);
      content: ' ';
    }

    &--2x {
      height: 10rem;
    }

    &:first-of-type {
      border: none;
    }
  }
}

@include atM {
  :deep(.ui-header) {
    margin-bottom: 9rem;
  }

  :deep(.bs-tabs__list) {
    flex-direction: row;
    gap: 0.8rem;
    margin: 0 auto 5.6rem auto;
    padding: 0.8rem;
    justify-self: center;
    background-color: $white;
  }

  :deep(.bs-tabs__button) {
    padding: 1.6rem 3.2rem;
  }

  .section__col--link {
    @include h9;
    display: inline-block;
    margin: 0 1.6rem;
  }

  .section__col--img {
    img {
      max-width: 131.3rem;
    }
  }

  .report-legend {
    h4 {
      @include h9;
      margin-bottom: 4rem;
    }

    &__item-cell {
      @include h9;

      &--icon {
        img {
          width: 4.8rem;
          height: 4.8rem;
        }
      }
    }
  }

  .negative-effect {
    $root: &;
    max-width: 90%;

    &__heading {
      width: 70%;
      padding: 3.2rem;
    }

    &__row {
      flex-direction: row;
    }

    &__col {

      &--0 {
        #{$root}__spacer {
          margin-right: 3.2rem;
          margin-left: auto;
        }
      }

      &--2 {
        #{$root}__spacer {
          margin-right: auto;
          margin-left: 3.2rem;
        }
      }
    }

    &__item {
      &--0 {
        width: 100%;
        min-height: 15rem;
      }

      &--1 {
        width: 100%;
        background-color: $purple-500;
      }
    }

    &__spacer {
      &:first-of-type {
        border-left: 1px dashed $gray-500;
      }
    }
  }
}
</style>
